import { memo, useEffect, useState } from 'react';
import { fetchWithAuth } from '../../../../js/authToken';
import API_BASE_URL from '../../../../js/urlHelper';

const LoansFilters = ({ filters, onChange, onApply, onReset, loading, errors = {}, formError = '' }) => {
  const [asesores, setAsesores] = useState([]);
  const [loadingAsesores, setLoadingAsesores] = useState(true);
  const [errorAsesores, setErrorAsesores] = useState('');
  const isApplyDisabled = loading;

  useEffect(() => {
    const fetchAsesores = async () => {
      try {
        setLoadingAsesores(true);
        const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/asesores`);
        const data = await response.json();
        setAsesores(data.asesores || []);
        setErrorAsesores('');
      } catch (error) {
        setErrorAsesores('No se pudieron cargar los asesores. Intente de nuevo más tarde.');
        setAsesores([]);
      } finally {
        setLoadingAsesores(false);
      }
    };
    fetchAsesores();
  }, []);

  const getErrorMessage = (error) => {
    if (!error) return '';
    return Array.isArray(error) ? error[0] : error;
  };

  const fieldClass = (hasError) =>
    `w-full rounded-2xl border bg-white px-4 py-3 text-sm text-gray-900 shadow-sm transition
     ${hasError
       ? 'border-red-400 ring-2 ring-red-200 focus:ring-red-300 focus:border-red-400'
       : 'border-gray-200 focus:ring-2 focus:ring-red-200 focus:border-red-300'}`;

  return (
    <section className="space-y-4">
      {/* Banda de error del formulario (422) */}
      {!!formError && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-sm" role="alert" aria-live="polite">
          {formError}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 items-start">
        {/* FECHA INICIO */}
        <div>
          <label htmlFor="date_start" className="block text-xs font-semibold uppercase tracking-[0.2em] text-gray-500 mb-2">
            FECHA INICIO
          </label>
          <input
            id="date_start"
            name="date_start"
            type="date"
            value={filters.date_start}
            onChange={onChange}
            className={fieldClass(!!errors.date_start)}
            aria-invalid={!!errors.date_start}
            aria-describedby={errors.date_start ? 'date_start_error' : undefined}
          />
          <div className="h-5">
            {!!errors.date_start && (
              <p id="date_start_error" className="mt-1 text-xs text-red-600">
                {getErrorMessage(errors.date_start)}
              </p>
            )}
          </div>
        </div>

        {/* FECHA FIN */}
        <div>
          <label htmlFor="date_end" className="block text-xs font-semibold uppercase tracking-[0.2em] text-gray-500 mb-2">
            FECHA FIN
          </label>
          <input
            id="date_end"
            name="date_end"
            type="date"
            value={filters.date_end}
            onChange={onChange}
            className={fieldClass(!!errors.date_end)}
            aria-invalid={!!errors.date_end}
            aria-describedby={errors.date_end ? 'date_end_error' : undefined}
          />
          <div className="h-5">
            {!!errors.date_end && (
              <p id="date_end_error" className="mt-1 text-xs text-red-600">
                {getErrorMessage(errors.date_end)}
              </p>
            )}
          </div>
        </div>

        {/* ASESOR */}
        <div>
          <label htmlFor="advisor" className="block text-xs font-semibold uppercase tracking-[0.2em] text-gray-500 mb-2">
            ASESOR
          </label>
          <select
            id="advisor"
            name="advisor"
            value={filters.advisor || ''}
            onChange={onChange}
            disabled={loadingAsesores}
            className={`${fieldClass(!!errors.advisor)} ${loadingAsesores ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            aria-invalid={!!errors.advisor}
            aria-describedby={errors.advisor ? 'advisor_error' : undefined}
          >
            {loadingAsesores ? (
              <option value="">Cargando asesores...</option>
            ) : (
              <>
                <option value="">TODOS</option>
                {asesores.map(asesor => (
                  <option key={asesor.id} value={asesor.id}>
                    {asesor.nombre}
                  </option>
                ))}
              </>
            )}
          </select>
          <div className="h-5">
            {!!errors.advisor && (
              <p id="advisor_error" className="mt-1 text-xs text-red-600">
                {getErrorMessage(errors.advisor)}
              </p>
            )}
            {!!errorAsesores && !errors.advisor && (
              <p className="mt-1 text-xs text-red-600">{errorAsesores}</p>
            )}
          </div>
        </div>

        {/* ESTADO */}
        <div>
          <label htmlFor="estado" className="block text-xs font-semibold uppercase tracking-[0.2em] text-gray-500 mb-2">
            ESTADO
          </label>
          <select
            id="estado"
            name="estado"
            value={filters.estado || ''}
            onChange={onChange}
            className={fieldClass(!!errors.estado)}
            aria-invalid={!!errors.estado}
            aria-describedby={errors.estado ? 'estado_error' : undefined}
          >
            <option value="">TODOS</option>
            <option value="activo">ACTIVO</option>
            <option value="cancelado">CANCELADO</option>
          </select>
          <div className="h-5">
            {!!errors.estado && (
              <p id="estado_error" className="mt-1 text-xs text-red-600">
                {getErrorMessage(errors.estado)}
              </p>
            )}
          </div>
        </div>

        {/* ABONADO POR */}
        <div>
          <label htmlFor="abonado_por" className="block text-xs font-semibold uppercase tracking-[0.2em] text-gray-500 mb-2">
            ABONADO POR
          </label>
          <select
            id="abonado_por"
            name="abonado_por"
            value={filters.abonado_por || ''}
            onChange={onChange}
            className={fieldClass(!!errors.abonado_por)}
            aria-invalid={!!errors.abonado_por}
            aria-describedby={errors.abonado_por ? 'abonado_por_error' : undefined}
          >
            <option value="">TODOS</option>
            <option value="CUENTA CORRIENTE">CUENTA CORRIENTE</option>
            <option value="CAJA CHICA">CAJA CHICA</option>
          </select>
          <div className="h-5">
            {!!errors.abonado_por && (
              <p id="abonado_por_error" className="mt-1 text-xs text-red-600">
                {getErrorMessage(errors.abonado_por)}
              </p>
            )}
          </div>
        </div>

        {/* Acciones - ocupa toda la fila en mobile y 2 columnas en desktop */}
        <div className="flex flex-col sm:flex-row gap-3 lg:col-span-5">
          <button
            type="button"
            onClick={onApply}
            disabled={isApplyDisabled}
            aria-busy={loading}
            className="w-full rounded-full bg-red-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-red-700 hover:shadow-md disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {loading ? 'Cargando…' : 'Aplicar'}
          </button>

          <button
            type="button"
            onClick={onReset}
            disabled={loading}
            className="w-full rounded-full border border-red-200 bg-white px-6 py-3 text-sm font-semibold text-red-600 shadow-sm transition hover:border-red-300 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-70"
          >
            Limpiar
          </button>
        </div>
      </div>
    </section>
  );
};

export default memo(LoansFilters);
